// src/app/components/inventory/models/inventory.model.ts

/** The three license-key types tracked per organization. */
export type InvKeyType = 'individual' | 'group' | 'team';

export interface KeyBalances {
  individual: number;   // Individual profile keys
  group: number;        // Group profile plus keys
  team: number;         // Team report keys
}

/** An organization, or the Unassigned bucket — both hold balances and both can transfer. */
export interface TransferParty {
  id: string;
  name: string;
}

/**
 * A key transfer between two buckets.
 *
 * Unassigned is a party like any other: somewhere to park keys that aren't yet attached to an
 * organization or buying account. It holds a real balance, so transferring out of it debits it —
 * it is not an infinite source.
 */
export interface TransferRequest {
  keyType: InvKeyType;
  amount: number;
  from: TransferParty;
  to: TransferParty;
}

export interface OrgInventoryMeta {
  results: number;                       // "Results in your account"
  pricing: 'Business' | 'Standard';      // shown as "Tier"
  buyingType: 'Reseller' | 'Direct';
  buyer: string;                         // buyer account email/handle
  unlimitedTeamReports: boolean;         // team reports are uncapped — keys stop applying
}

export interface InventoryTxn {
  id: string;
  ref: string;                           // e.g. '235878'
  keyType: InvKeyType;
  type: 'Transfer' | 'Profile';
  date: string;                          // ISO
  item: string;                          // 'Profile keys' | 'Group profile plus keys' | 'Team report keys'
  org: string;                           // counterparty / target label
  description: string;                   // e.g. 'from Unassigned'
  qty: number;                           // signed net qty (+in / -out)
}

export interface OrgInventory {
  orgId: string;
  orgName: string;
  balances: KeyBalances;
  meta: OrgInventoryMeta;
  ledger: InventoryTxn[];
}

export const KEY_LABELS: Record<InvKeyType, string> = {
  individual: 'Individual profile keys',
  group: 'Group profile plus keys',
  team: 'Team report keys',
};

export const KEY_ITEM_LABELS: Record<InvKeyType, string> = {
  individual: 'Profile keys',
  group: 'Group profile plus keys',
  team: 'Team report keys',
};
